import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Input, Input as AntdInput, message, Modal, Tabs } from 'antd';

import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';

import styles from '../styles/dashboard.module.scss';

import { Header } from '../components';
import Workspaces from '../components/Workspaces/index.jsx';
import UploadAvatar from '../components/UploadAvatar/index.jsx';
import { useDeleteUserMutation, useGetUserQuery, useUpdateUserMutation } from '../store/api/user';
import { setAvatar } from '../store/slices/auth';
import { useViewport, useLogout } from '../hooks';
import { BreakpointMobile, BreakpointMobileUp } from '../constants/ui';
import { LocalizationSelect } from '../components/Localization/index.jsx';
import { ProfileDeleteModal } from '../components/Profile/index.jsx';
import { getSidebarLinksMap } from '../utils/ui';
import { getPasswordRule } from '../utils/validation';

const ProfilePage = ({ type }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { widthViewport } = useViewport();
  const { t } = useTranslation();
  const SidebarLinksMap = getSidebarLinksMap(t);
  const tabsLabels = t('profile.tabs', { returnObjects: true });

  const { currentUserId } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const [avatarFile, setAvatarFile] = useState(null);
  const [submittable, setSubmittable] = useState(false);
  const [userData, setUserData] = useState({ firstName: '', lastName: '', position: '', userPhone: '' });
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const values = Form.useWatch([], form);

  const { isSuccess: isUserSuccess, data: user } = useGetUserQuery({ userId: currentUserId });
  const [updateUserData, { isError, error, isSuccess, data }] = useUpdateUserMutation();
  const [deleteUser, { isSuccess: isDeleteUserSuccess }] = useDeleteUserMutation();

  const logoutUser = useLogout();

  const avatarSubmittedRef = useRef(false);
  const dataSubmittedRef = useRef(false);

  const onSubmit = () => {
    if (currentUserId) {
      //update avatar, position, phone, password, name
      const isAvatarDeleted = avatarFile === '' && !!user.avatar;
      const isAvatarChanged = avatarFile instanceof File || isAvatarDeleted;
      const isPasswordChanged =
        values.oldPassword && values.newPassword && values.confirmPassword === values.newPassword;
      const isFirstNameChanged = values.firstName !== userData.firstName;
      const isLastNameChanged = values.lastName !== userData.lastName;
      const isPositionChanged = values.position !== userData.position;
      const isPhoneChanged = values.userPhone !== userData.userPhone;

      const isDataChanged =
        isPasswordChanged || isFirstNameChanged || isLastNameChanged || isPositionChanged || isPhoneChanged;

      if (isAvatarChanged || isDataChanged) {
        avatarSubmittedRef.current = isAvatarChanged;
        dataSubmittedRef.current = isDataChanged;

        let newData = {
          userId: currentUserId,
        };
        if (isAvatarChanged) {
          newData = {
            ...newData,
            avatar: avatarFile,
          };
        }
        if (isFirstNameChanged) {
          newData = {
            ...newData,
            first_name: values.firstName,
          };
        }
        if (isLastNameChanged) {
          newData = {
            ...newData,
            last_name: values.lastName,
          };
        }
        if (isPositionChanged) {
          newData = {
            ...newData,
            position: values.position,
          };
        }
        if (isPhoneChanged) {
          newData = {
            ...newData,
            phone_number: values.userPhone,
          };
        }
        if (isPasswordChanged) {
          newData = {
            ...newData,
            old_password: values.oldPassword,
            new_password: values.newPassword,
          };
        }
        updateUserData(newData);
      }
    }
  };

  const changeImage = (file) => {
    setAvatarFile(file ? file : '');
  };

  const onChangeTab = () => {
    navigate({
      pathname: type === 'profile' ? `/workspaces` : `/profile`,
    });
  };

  const onBackToDashboard = () => {
    navigate('/sensors');
  };

  const onDeleteAccount = () => {
    deleteUser({ userId: currentUserId });
    setIsDeleteAccountModalOpen(false);
  };

  const onCloseDeleteAccountModal = () => {
    setIsDeleteAccountModalOpen(false);
  };

  // Get user data
  useEffect(() => {
    if (isUserSuccess) {
      const newData = {
        firstName: user.first_name,
        lastName: user.last_name,
        position: user.position,
        userPhone: user.phone_number,
      };
      setUserData(newData);
      form.setFieldsValue(newData);
    }
  }, [isUserSuccess, user]);

  // User data updated (success)
  useEffect(() => {
    if (isSuccess) {
      setUserData({
        firstName: data.first_name,
        lastName: data.last_name,
        position: data.position,
        userPhone: data.phone_number,
      });
      form.resetFields(['oldPassword', 'confirmPassword', 'newPassword']);

      if (avatarSubmittedRef.current) {
        dispatch(setAvatar({ avatar: data.avatar }));
        setAvatarFile(null);
        message.info(t('profile.form.messageSuccessAvatar') || 'Avatar has been updated!');
      }
      if (dataSubmittedRef.current) {
        message.info(t('profile.form.messageSuccess') || 'Profile information has been updated!');
      }

      avatarSubmittedRef.current = false;
      dataSubmittedRef.current = false;
    }
  }, [data, isSuccess]);

  // Is submittable
  useEffect(() => {
    if (values) {
      const isActive =
        avatarFile instanceof File ||
        (avatarFile === '' && !!user?.avatar) ||
        values.firstName !== userData.firstName ||
        values.lastName !== userData.lastName ||
        values.position !== userData.position ||
        values.userPhone !== userData.userPhone ||
        (!!values.oldPassword && !!values.newPassword && values.confirmPassword === values.newPassword);
      setSubmittable(isActive);
    }
  }, [avatarFile, values, userData]);

  // User data updated (error)
  useEffect(() => {
    if (isError) {
      const [{ attr, detail }] = error;

      if (attr === 'old_password') {
        const field = form.getFieldValue('oldPassword');
        form.setFields([{ name: 'oldPassword', value: field, errors: [detail] }]);
      }
      if (attr === 'phone_number') {
        const field = form.getFieldValue('userPhone');
        form.setFields([{ name: 'userPhone', value: field, errors: [detail] }]);
      }
    }
  }, [error, form, isError]);

  // Current account has been deleted
  useEffect(() => {
    if (isDeleteUserSuccess) {
      // dispatch(logout());
      // window.location.href = '/login';
      logoutUser().then();
    }
  }, [isDeleteUserSuccess]);

  return (
    <>
      {widthViewport > BreakpointMobile && <Header t={t} title={SidebarLinksMap.profile.title} />}
      <main className="main">
        <div className={styles.pageHeader}>
          <div className={styles.pageTitleWrapper}>
            <Button
              type="text"
              icon={<CroplerIcon name="prev" />}
              className={styles.backButton}
              onClick={onBackToDashboard}
            ></Button>
            <UploadAvatar imageUrl={user ? user.avatar : ''} changeImage={changeImage} showUserAvatar={true} />
            {widthViewport < BreakpointMobileUp && (userData.lastName || userData.firstName) && (
              <div className={styles.pageTitleDescription}>
                <span>{t('profile.subtitleMobile')}</span>
                <span className={styles.pageTitleDescriptionName}>
                  {userData.firstName} {userData.lastName}
                </span>
              </div>
            )}
          </div>
          {widthViewport > BreakpointMobile && (
            <Button
              type="primary"
              icon={<CroplerIcon name="check" />}
              onClick={() => onSubmit()}
              disabled={!submittable}
            >
              {t('actions.saveChanges')}
            </Button>
          )}
          {widthViewport < BreakpointMobileUp && (
            <Button type="primary" onClick={() => onSubmit()} disabled={!submittable}>
              {t('actions.save')}
            </Button>
          )}
        </div>
        <Tabs
          defaultActiveKey={type}
          size="small"
          style={{ flexGrow: 1 }}
          tabBarStyle={{
            paddingLeft: 24,
            paddingRight: 24,
            backgroundColor: 'var(--main)',
            margin: 0,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
          items={[
            {
              key: 'profile',
              label: tabsLabels[0],
              children: (
                <div className={styles.profileFormContainer}>
                  <Form
                    className={`${styles.profileForm} profileForm`}
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    autoComplete="off"
                  >
                    {/* User data */}
                    <div className={styles.profileFormGroup}>
                      <div className={styles.profileFormRow}>
                        <Form.Item
                          label={t('profile.form.name')}
                          name="firstName"
                          rules={[
                            {
                              required: true,
                              type: 'string',
                              max: 150,
                              message: t('forms.validation.enterName'),
                            },
                          ]}
                        >
                          <Input type="text" size={'large'} />
                        </Form.Item>
                        <Form.Item
                          label={t('profile.form.lastName')}
                          name="lastName"
                          rules={[
                            {
                              required: true,
                              type: 'string',
                              max: 150,
                              message: t('forms.validation.enterName'),
                            },
                          ]}
                        >
                          <Input type="text" size={'large'} />
                        </Form.Item>
                        <Form.Item
                          label={t('profile.form.position')}
                          name="position"
                          rules={[
                            {
                              type: 'string',
                              max: 128,
                            },
                          ]}
                        >
                          <Input type="text" size={'large'} />
                        </Form.Item>
                        <Form.Item
                          label={t('profile.form.phone')}
                          name="userPhone"
                          rules={[
                            {
                              type: 'string',
                              max: 128,
                            },
                          ]}
                        >
                          <Input type="phone" size={'large'} />
                        </Form.Item>
                      </div>
                    </div>

                    {/* Password */}
                    <div className={styles.profileFormGroup}>
                      <h2 className={styles.profileFormTitle}>{t('profile.password.title')}</h2>
                      <div className={styles.profileFormRow}>
                        <Form.Item
                          label={t('profile.password.form.old')}
                          name="oldPassword"
                          rules={[
                            {
                              required: true,
                              type: 'password',
                              message: t('forms.validation.enterPassword'),
                            },
                          ]}
                        >
                          <AntdInput.Password placeholder={t('profile.password.form.old')} size="large" />
                        </Form.Item>
                        <Form.Item
                          label={t('profile.password.form.new')}
                          name="newPassword"
                          rules={[
                            {
                              required: true,
                              type: 'password',
                              message: '',
                            },
                            getPasswordRule(t),
                          ]}
                        >
                          <AntdInput.Password placeholder={t('profile.password.form.new')} size="large" />
                        </Form.Item>
                        <Form.Item
                          label={t('profile.password.form.confirm')}
                          name="confirmPassword"
                          rules={[
                            {
                              required: true,
                              type: 'password',
                              message: '',
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value || getFieldValue('newPassword') === value) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error(t('profile.password.form.error')));
                              },
                            }),
                          ]}
                        >
                          <AntdInput.Password placeholder={t('profile.password.form.confirm')} size="large" />
                        </Form.Item>
                      </div>
                      <p dangerouslySetInnerHTML={{ __html: t('password.text') }}></p>
                      <ul>
                        <li dangerouslySetInnerHTML={{ __html: t('password.condition1') }}></li>
                        <li dangerouslySetInnerHTML={{ __html: t('password.condition2') }}></li>
                      </ul>
                    </div>

                    {/* Language */}
                    <div className={styles.profileFormGroup}>
                      <h2 className={styles.profileFormTitle}>{t('profile.language.title')}</h2>
                      <div className={styles.profileFormRow}>
                        <LocalizationSelect />
                      </div>
                    </div>

                    {/* Email */}
                    <div className={styles.profileFormGroup}>
                      <h2 className={styles.profileFormTitle}>{t('profile.email.title')}</h2>
                      <p dangerouslySetInnerHTML={{ __html: t('profile.email.text') }}></p>
                    </div>

                    {/* Delete account */}
                    <div className={styles.profileFormNav}>
                      <h2 className={styles.profileFormTitle}>{t('profile.delete.title')}</h2>
                      <p dangerouslySetInnerHTML={{ __html: t('profile.delete.text') }}></p>
                      <Button
                        type="text-danger"
                        size={'large'}
                        icon={<CroplerIcon name="remove" />}
                        onClick={() => setIsDeleteAccountModalOpen(true)}
                      >
                        {t('profile.delete.title')}
                      </Button>
                    </div>
                  </Form>
                </div>
              ),
            },
            {
              key: 'workspaces',
              label: tabsLabels[1],
              children: <Workspaces />,
            },
          ]}
          onTabClick={onChangeTab}
        />
      </main>

      <Modal
        title={t('common.areYouSure')}
        open={isDeleteAccountModalOpen}
        onCancel={() => onCloseDeleteAccountModal()}
        footer={false}
        centered
        width={450}
      >
        <ProfileDeleteModal onDelete={() => onDeleteAccount()} onCancel={() => onCloseDeleteAccountModal()} />
      </Modal>
    </>
  );
};

export default ProfilePage;
